/**
 * Utilitarios para calcular y sincronizar el reloj del partido en vivo
 * Implementación EXACTA según especificaciones de la documentación oficial
 */

// Minuto en que arranca cada periodo de juego y su duración reglamentaria
const PERIOD_BASE = { 1: 0, 2: 45, 3: 90, 4: 105 };
const PERIOD_LEN = { 1: 45, 2: 45, 3: 15, 4: 15 };

// Títulos de periodos según period_id
const PERIOD_TITLES = {
  1: "Primer tiempo",
  2: "Segundo tiempo",
  3: "Primer tiempo extra",
  4: "Segundo tiempo extra",
  5: "Penales",
  10: "Medio tiempo",
  11: "Fin del 2º tiempo",
  12: "Descanso tiempo extra",
  13: "Fin tiempo extra",
};

// Variable global para almacenar el offset del reloj del servidor (ms que el servidor va adelante)
let clockOffset = null;

/**
 * Sincroniza el reloj del dispositivo con el del servidor
 * Implementación EXACTA de la especificación
 * @param {Object} soccerData - Objeto SoccerData del API
 * @param {number} receivedAt - Timestamp en ms cuando se recibió la respuesta
 */
export const syncClock = (soccerData, receivedAt = Date.now()) => {
  if (!soccerData?.server_time) return;
  
  try {
    const serverTime = Date.parse(soccerData.server_time);
    if (isNaN(serverTime)) return;
    
    const offset = serverTime - receivedAt;
    
    // Una respuesta del caché trae un server_time viejo: quedarse con el mayor
    clockOffset = clockOffset === null ? offset : Math.max(clockOffset, offset);
    
    console.debug(`[Clock] sync: offset=${offset}ms, cumulative=${clockOffset}ms`);
  } catch (error) {
    console.error("[Clock] Error en syncClock:", error);
  }
};

/**
 * Resetea el offset del reloj (útil para limpiar en unmount o nuevas sesiones)
 */
export const resetClockOffset = () => {
  console.debug("[Clock] reset offset");
  clockOffset = null;
};

/**
 * Obtiene el offset actual del reloj
 * @returns {number|null} Offset en ms o null si no está sincronizado
 */
export const getClockOffset = () => clockOffset;

/**
 * Formatea segundos a formato "M:SS" (coincide con especificación)
 * @param {number} s - Segundos a formatear
 * @returns {string} Formato "45:30" o "2:05"
 */
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * Calcula el reloj del partido - IMPLEMENTACIÓN EXACTA DE LA ESPECIFICACIÓN
 * @param {Object} soccerData - { period_id, period_start }
 * @returns {string|null} Reloj formateado ("67:39", "90+2:13") o null
 */
export const matchClock = (soccerData) => {
  if (!soccerData?.period_start || !(soccerData?.period_id in PERIOD_BASE)) {
    return null; // pausa o penales
  }

  const { period_id, period_start } = soccerData;

  try {
    // Hora actual corregida con offset del servidor
    const now = Date.now() + (clockOffset ?? 0);
    
    // Tiempo transcurrido en segundos desde el inicio del periodo
    const elapsed = Math.max(0, Math.floor((now - Date.parse(period_start)) / 1000));
    
    // Minuto base y límite
    const base = PERIOD_BASE[period_id] * 60;
    const limit = PERIOD_LEN[period_id] * 60;

    // Si no hemos alcanzado el final del periodo
    if (elapsed < limit) {
      return fmt(base + elapsed); // "67:39"
    }

    // Pasamos el límite, mostrar compensación
    return `${(base + limit) / 60}+${fmt(elapsed - limit)}`; // "90+2:13"
  } catch (error) {
    console.error("[Clock] Error en matchClock:", error);
    return null;
  }
};
/**
 * Obtiene el título del periodo actual
 * @param {number} period_id - ID del periodo desde SoccerData
 * @returns {string} Título del periodo ("Primer tiempo", "Medio tiempo", etc.)
 */
export const getPeriodTitle = (period_id) => {
  return PERIOD_TITLES[period_id] || "Período desconocido";
};

/**
 * Determina si el partido está en juego (no en pausa)
 * Periodos 1-5 son de juego, 10-13 son pausas
 * @param {number} period_id - ID del periodo desde SoccerData
 * @returns {boolean}
 */
export const isMatchInProgress = (period_id) => {
  return period_id >= 1 && period_id <= 5;
};

/**
 * Obtiene la compensación anunciada formateada para mostrar
 * @param {number} announced_injury_time - Segundos de compensación
 * @returns {string} Formato "+4"
 */
export const formatInjuryTime = (announced_injury_time) => {
  if (!announced_injury_time) return "";
  
  const minutes = Math.floor(announced_injury_time / 60);
  const sign = minutes >= 0 ? "+" : "-";
  return `${sign}${Math.abs(minutes)}`;
};

/**
 * Función auxiliar para obtener todos los datos del reloj
 * @param {Object} soccerData - Datos de SoccerData del API
 * @returns {Object} Objeto con toda la información del reloj
 */
export const useMatchClock = (soccerData) => {
  if (!soccerData?.period_id) {
    return {
      clock: null,
      periodTitle: null,
      isInProgress: false,
      injuryTimeFormatted: "",
      periodId: null,
      isOnBreak: false,
    };
  }

  const { period_id, announced_injury_time } = soccerData;
  const clock = matchClock(soccerData);
  const isInProgress = isMatchInProgress(period_id);
  const isOnBreak = !clock && isInProgress;

  return {
    clock,
    periodTitle: getPeriodTitle(period_id),
    isInProgress,
    injuryTimeFormatted: formatInjuryTime(announced_injury_time),
    periodId: period_id,
    isOnBreak,
  };
};

export default {
  syncClock,
  resetClockOffset,
  getClockOffset,
  matchClock,
  getPeriodTitle,
  isMatchInProgress,
  formatInjuryTime,
  useMatchClock,
};
